import { useEffect, useMemo, useState } from "react";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import { Form as BsForm } from "react-bootstrap";
import { toast } from "react-hot-toast";
import InvestorModal, { InvestorModalActions } from "./InvestorModal";
import OnlinePaymentRailsChooser from "./OnlinePaymentRailsChooser";
import { TextInputComp } from "..";
import { formatMoney } from "../../utils/currencyFormat";
import { getMemberTransactionChargesTotal } from "../../utils/memberTransactionCharges";
import {
  useCreateMarketplaceOrderMutation,
  useMarketplaceOrderFeePreviewMutation,
} from "../../store/investor/investorMarketplaceApi";
import { useInvestorSession } from "../../hooks/useInvestorSession";
import {
  getFrontendBaseUrl,
  redirectToMemberOnlinePayment,
} from "../../utils/memberOnlinePayment";

function resolveContactProfile(profile) {
  const fullName =
    profile?.full_name ||
    profile?.fullName ||
    profile?.name ||
    `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() ||
    "";
  return {
    fullName: fullName.trim(),
    phone: String(profile?.phone || "").trim(),
    email: String(profile?.email || "").trim(),
  };
}

function QuantityField({ name, minQty, maxQty }) {
  const [field, meta, helpers] = useField(name);

  return (
    <div className="mb-3">
      <BsForm.Label className="small fw-semibold">Quantity</BsForm.Label>
      <BsForm.Control
        className="py-2"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        name={field.name}
        value={field.value ?? ""}
        onBlur={field.onBlur}
        isInvalid={meta.touched && Boolean(meta.error)}
        onChange={(e) => {
          const digitsOnly = String(e.target.value || "").replace(/\D/g, "");
          helpers.setValue(digitsOnly === "" ? "" : Number(digitsOnly));
        }}
        onKeyDown={(e) => {
          if (
            e.ctrlKey ||
            e.metaKey ||
            e.altKey ||
            [
              "Backspace",
              "Delete",
              "Tab",
              "Escape",
              "Enter",
              "ArrowLeft",
              "ArrowRight",
              "Home",
              "End",
            ].includes(e.key)
          ) {
            return;
          }
          if (!/^\d$/.test(e.key)) {
            e.preventDefault();
          }
        }}
      />
      {maxQty != null ? (
        <p className="small text-muted mb-0 mt-1">
          Allowed: {minQty}–{maxQty}
        </p>
      ) : (
        <p className="small text-muted mb-0 mt-1">Minimum: {minQty}</p>
      )}
      {meta.touched && meta.error ? (
        <small className="text-danger d-block mt-1">{meta.error}</small>
      ) : null}
    </div>
  );
}

function FeePreviewPanel({ feePreview, isFetching, product, quantity }) {
  const principalFallback = (Number(product?.priceNgn) || 0) * (Number(quantity) || 0);
  const subtotal = feePreview?.subtotal ?? principalFallback;
  const deliveryFee = feePreview?.delivery_fee ?? 0;
  const charges = getMemberTransactionChargesTotal(feePreview?.fee_breakdown || feePreview);
  const total =
    feePreview?.payment_total ??
    feePreview?.fee_breakdown?.member_payable_total ??
    subtotal + deliveryFee + charges;

  return (
    <div className="bg-light rounded-3 p-3 mb-3">
      {isFetching ? (
        <div aria-busy="true">
          <div className="d-flex justify-content-between mb-2">
            <span className="placeholder col-4 rounded" style={{ height: 12 }} />
            <span className="placeholder col-2 rounded" style={{ height: 12 }} />
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="placeholder col-3 rounded" style={{ height: 12 }} />
            <span className="placeholder col-2 rounded" style={{ height: 12 }} />
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="placeholder col-4 rounded" style={{ height: 12 }} />
            <span className="placeholder col-2 rounded" style={{ height: 12 }} />
          </div>
          <div className="d-flex justify-content-between border-top pt-2 mt-2">
            <span className="placeholder col-5 rounded" style={{ height: 14 }} />
            <span className="placeholder col-3 rounded" style={{ height: 14 }} />
          </div>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between small mb-1">
            <span>Subtotal</span>
            <span>{formatMoney(subtotal)}</span>
          </div>
          <div className="d-flex justify-content-between small mb-1">
            <span>Delivery fee</span>
            <span>{formatMoney(deliveryFee)}</span>
          </div>
          <div className="d-flex justify-content-between small mb-1">
            <span>Payment charge</span>
            <span>{formatMoney(feePreview?.payment_fee ?? charges)}</span>
          </div>
          <div className="d-flex justify-content-between small fw-semibold border-top pt-2 mt-2">
            <span>Total payable</span>
            <span>{formatMoney(total)}</span>
          </div>
          <p className="small text-muted mb-0 mt-2">
            Stock is held briefly while you complete online checkout. If payment is not completed
            in time, the hold is released automatically.
          </p>
        </>
      )}
    </div>
  );
}

function FeePreviewWatcher({
  enabled,
  productId,
  quantity,
  minQty,
  maxQty,
  feePreviewMutation,
}) {
  useEffect(() => {
    if (!enabled) return;
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty < minQty) return;
    if (maxQty != null && qty > maxQty) return;

    const timer = setTimeout(() => {
      feePreviewMutation({
        items: [{ product_id: productId, quantity: qty }],
        delivery_fee: 0,
      }).catch(() => {});
    }, 300);

    return () => clearTimeout(timer);
  }, [enabled, productId, quantity, minQty, maxQty, feePreviewMutation]);

  return null;
}

/**
 * Marketplace checkout: Book Order or Pay Online (when online_payment_available).
 */
export default function MarketplaceOrderModal({
  show,
  product,
  profile: profileProp,
  onClose,
  onSuccess,
}) {
  const { profile: sessionProfile } = useInvestorSession();
  const profile = profileProp || sessionProfile;
  const contact = resolveContactProfile(profile);
  const [createOrder, { isLoading }] = useCreateMarketplaceOrderMutation();
  const [feePreviewMutation, { data: feePreview, isLoading: isFeeFetching }] =
    useMarketplaceOrderFeePreviewMutation();
  const [checkoutMode, setCheckoutMode] = useState("book");
  const [paymentProvider, setPaymentProvider] = useState("paystack");

  const minQty = product?.minimumQuantity ?? 1;
  const maxQty = product?.maximumQuantity;
  // Prefer product flag; fall back to fee-preview so list-card / stale cache still unlocks Pay Online.
  const onlineDisabled =
    product?.onlinePaymentAvailable !== true &&
    feePreview?.online_payment_available !== true;

  useEffect(() => {
    if (onlineDisabled && checkoutMode === "pay_online") {
      setCheckoutMode("book");
    }
  }, [onlineDisabled, checkoutMode]);

  // Warm fee preview when modal opens so Pay Online availability can resolve from API.
  useEffect(() => {
    if (!show || !product?.id) return;
    const qty = Number(product.minimumQuantity ?? 1) || 1;
    feePreviewMutation({
      items: [{ product_id: product.id, quantity: qty }],
      delivery_fee: 0,
    }).catch(() => {});
  }, [show, product?.id, product?.minimumQuantity, feePreviewMutation]);

  const schema = useMemo(() => {
    let quantity = Yup.number()
      .typeError("Quantity must be a number")
      .integer("Quantity must be a whole number")
      .min(minQty, `Minimum quantity is ${minQty}`)
      .required("Quantity is required");
    if (maxQty != null) {
      quantity = quantity.max(maxQty, `Maximum quantity is ${maxQty}`);
    }

    return Yup.object({
      quantity,
      delivery_method: Yup.string().oneOf(["delivery", "pickup"]).required(),
      line1: Yup.string().when("delivery_method", {
        is: "delivery",
        then: Yup.string().required("Street address is required"),
        otherwise: Yup.string().nullable(),
      }),
      line2: Yup.string().nullable(),
      city: Yup.string().when("delivery_method", {
        is: "delivery",
        then: Yup.string().required("City is required"),
        otherwise: Yup.string().nullable(),
      }),
      state: Yup.string().nullable(),
      postal_code: Yup.string().nullable(),
      pickup_location: Yup.string().when("delivery_method", {
        is: "pickup",
        then: Yup.string().required("Select a pickup location"),
        otherwise: Yup.string().nullable(),
      }),
      customer_note: Yup.string().nullable(),
    });
  }, [minQty, maxQty]);

  const pickupLocations = useMemo(() => {
    if (Array.isArray(product?.pickupLocations) && product.pickupLocations.length) {
      return product.pickupLocations.filter((l) => String(l || "").trim());
    }
    return product?.pickupLocation ? [product.pickupLocation] : [];
  }, [product]);

  const initialValues = useMemo(
    () => ({
      quantity: minQty,
      delivery_method: product?.deliveryAvailable !== false ? "delivery" : "pickup",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postal_code: "",
      pickup_location: pickupLocations[0] || "",
      customer_note: "",
    }),
    [product, minQty, pickupLocations]
  );

  const pickupOffered = Boolean(product?.pickupAvailable && pickupLocations.length > 0);

  if (!product) return null;

  return (
    <InvestorModal show={show} onClose={onClose} title="Place order" size="lg">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={schema}
        onSubmit={async (values) => {
          if (checkoutMode === "pay_online" && onlineDisabled) {
            toast.error("Online payment is not available. Please book this order instead.");
            return;
          }
          if (checkoutMode === "pay_online" && !paymentProvider) {
            toast.error("Select a payment method.");
            return;
          }

          if (!contact.fullName || !contact.phone) {
            toast.error(
              "Your profile is missing a name or phone number. Update your profile and try again."
            );
            return;
          }

          const qty = Number(values.quantity);
          if (!Number.isFinite(qty) || qty < minQty) {
            toast.error(`Minimum quantity is ${minQty}`);
            return;
          }
          if (maxQty != null && qty > maxQty) {
            toast.error(`Maximum quantity is ${maxQty}`);
            return;
          }

          try {
            const body = {
              checkout_mode: checkoutMode,
              items: [{ product_id: product.id, quantity: qty }],
              full_name: contact.fullName,
              phone: contact.phone,
              email: contact.email || null,
              contact_method: "phone",
              delivery_method: values.delivery_method,
              customer_note: values.customer_note || null,
              delivery_fee: 0,
            };
            if (checkoutMode === "pay_online") {
              body.payment_provider = paymentProvider;
              body.frontend_base_url = getFrontendBaseUrl();
            }
            if (values.delivery_method === "delivery") {
              body.delivery_address = {
                line1: values.line1,
                line2: values.line2 || null,
                city: values.city,
                state: values.state || null,
                postal_code: values.postal_code || null,
                country: "NG",
              };
            } else {
              if (!pickupLocations.length) {
                toast.error(
                  "Pickup is not available for this product because no pickup location is configured."
                );
                return;
              }
              const chosen = String(values.pickup_location || "").trim();
              if (!chosen || !pickupLocations.includes(chosen)) {
                toast.error("Please select a valid pickup location.");
                return;
              }
              body.pickup_location = chosen;
            }

            const result = await createOrder(body).unwrap();

            if (checkoutMode === "pay_online") {
              const paymentUrl = result.paymentUrl;
              if (!paymentUrl) {
                toast.error(
                  result.message ||
                    "Payment could not be started. Please try again or book the order instead."
                );
                return;
              }
              if (result.paymentReference) {
                sessionStorage.setItem(
                  "pending_marketplace_payment_reference",
                  result.paymentReference
                );
              }
              if (result.order?.orderNumber) {
                sessionStorage.setItem(
                  "pending_marketplace_order_number",
                  result.order.orderNumber
                );
              }
              toast.success("Redirecting to secure payment…");
              onSuccess?.(result.order);
              redirectToMemberOnlinePayment({
                payment_url: paymentUrl,
                payment_reference: result.paymentReference,
                payment_provider: result.paymentProvider || paymentProvider,
              });
              return;
            }

            toast.success(
              "Your order has been submitted for confirmation. A confirmation email will be sent if an email is on your profile."
            );
            onSuccess?.(result.order);
            onClose();
          } catch (err) {
            const msg =
              err?.data?.message ||
              err?.error ||
              "We couldn’t submit your order. Please try again.";
            toast.error(msg);
          }
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => {
          const previewTotal = (Number(product.priceNgn) || 0) * (Number(values.quantity) || 0);

          return (
            <Form onSubmit={handleSubmit}>
              <FeePreviewWatcher
                enabled={checkoutMode === "pay_online" && !onlineDisabled && show}
                productId={product.id}
                quantity={values.quantity}
                minQty={minQty}
                maxQty={maxQty}
                feePreviewMutation={feePreviewMutation}
              />
              <p className="small text-muted mb-3">
                {product.name} · {formatMoney(product.priceNgn)} / {product.unit}
              </p>

              <div className="mb-3">
                <label className="form-label small fw-semibold">Checkout option</label>
                <div className="d-flex flex-column gap-2">
                  <label
                    className={`border rounded-3 p-3 mb-0 ${
                      checkoutMode === "book" ? "border-primary" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      name="checkout_mode"
                      checked={checkoutMode === "book"}
                      onChange={() => setCheckoutMode("book")}
                    />
                    <strong>Book Order</strong>
                    <div className="small text-muted mt-1 ms-4">
                      Submit your order request. Our team will confirm availability and the next
                      steps.
                    </div>
                  </label>
                  <label
                    className={`border rounded-3 p-3 mb-0 ${
                      checkoutMode === "pay_online" ? "border-primary" : ""
                    } ${onlineDisabled ? "opacity-75" : ""}`}
                    style={{ cursor: onlineDisabled ? "not-allowed" : "pointer" }}
                  >
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      name="checkout_mode"
                      checked={checkoutMode === "pay_online"}
                      disabled={onlineDisabled}
                      onChange={() => {
                        if (!onlineDisabled) setCheckoutMode("pay_online");
                      }}
                    />
                    <strong>Pay Online</strong>
                    <div className="small text-muted mt-1 ms-4">
                      {onlineDisabled
                        ? "Online payment is not available for this cooperative right now. You can book this order instead."
                        : "Pay securely online with the gateway enabled for your cooperative. Stock is held while you complete payment."}
                    </div>
                  </label>
                </div>
              </div>

              <QuantityField name="quantity" minQty={minQty} maxQty={maxQty} />

              <div className="mb-3">
                <label className="form-label small fw-semibold">Delivery method</label>
                <select
                  className="form-select"
                  value={values.delivery_method}
                  onChange={(e) => setFieldValue("delivery_method", e.target.value)}
                >
                  {product.deliveryAvailable !== false ? (
                    <option value="delivery">Delivery</option>
                  ) : null}
                  {pickupOffered ? <option value="pickup">Pickup</option> : null}
                </select>
                {product.pickupAvailable && !pickupOffered ? (
                  <p className="small text-muted mb-0 mt-1">
                    Pickup is not offered yet because the cooperative has not set a pickup
                    location.
                  </p>
                ) : null}
              </div>

              {values.delivery_method === "delivery" ? (
                <>
                  <TextInputComp
                    LabelText="Street address"
                    name="line1"
                    placeholder="House number and street"
                    required
                  />
                  <TextInputComp
                    LabelText="Apartment / landmark (optional)"
                    name="line2"
                    placeholder="Apartment, suite, landmark"
                  />
                  <TextInputComp LabelText="City" name="city" required />
                  <TextInputComp LabelText="State (optional)" name="state" />
                  <TextInputComp
                    LabelText="Postal code (optional)"
                    name="postal_code"
                    placeholder="e.g. 100001"
                  />
                </>
              ) : (
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Pickup location *</label>
                  {pickupLocations.length === 1 ? (
                    <div className="bg-light rounded-3 p-3 small">
                      <strong style={{ color: "#383838" }}>{pickupLocations[0]}</strong>
                      <p className="text-muted mb-0 mt-1">
                        Collect your order from this cooperative pickup location.
                      </p>
                    </div>
                  ) : (
                    <select
                      className="form-select"
                      value={values.pickup_location}
                      onChange={(e) => setFieldValue("pickup_location", e.target.value)}
                    >
                      <option value="">Select a pickup location</option>
                      {pickupLocations.map((location) => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <TextInputComp
                LabelText="Note to staff (optional)"
                name="customer_note"
                as="textarea"
                rows={2}
                placeholder="Any special instructions"
              />

              {checkoutMode === "pay_online" && !onlineDisabled ? (
                <>
                  <OnlinePaymentRailsChooser
                    paymentProvider={paymentProvider}
                    onPaymentProviderChange={setPaymentProvider}
                    compact
                  />
                  <FeePreviewPanel
                    feePreview={feePreview}
                    isFetching={isFeeFetching}
                    product={product}
                    quantity={values.quantity}
                  />
                </>
              ) : (
                <div className="bg-light rounded-3 p-3 mb-3">
                  <div className="d-flex justify-content-between small">
                    <span>Estimated total</span>
                    <strong>{formatMoney(previewTotal)}</strong>
                  </div>
                  <p className="small text-muted mb-0 mt-1">
                    Final totals are confirmed by the cooperative after submission.
                  </p>
                </div>
              )}

              <InvestorModalActions
                onCancel={onClose}
                onConfirm={handleSubmit}
                confirmText={
                  checkoutMode === "pay_online" ? "Pay now" : "Book Order"
                }
                confirmDisabled={
                  isLoading ||
                  (checkoutMode === "pay_online" &&
                    (onlineDisabled || !paymentProvider))
                }
                loading={isLoading}
              />
            </Form>
          );
        }}
      </Formik>
    </InvestorModal>
  );
}
